import { useEffect, useState } from "react";
import { useDragDraft } from "./draft";

interface SliderProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  /** Extra work on drag start — draft batching is already handled. */
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Opt out of automatic viewer draft batching (default on). */
  draft?: boolean;
  /** Override the displayed value text (e.g. "Auto"). */
  display?: (v: number) => string;
  unit?: string;
  disabled?: boolean;
  className?: string;
  /**
   * v42 — end captions printed under the two ends of the track (THIN ↔ BOLD,
   * SLIM ↔ SOLID). They name the direction of travel, which a bare number does
   * not: on the kit slider rather than at the two call sites that wanted it
   * first, so any slider can say what its ends mean.
   */
  minLabel?: string;
  maxLabel?: string;
  /**
   * Tick ruler under the track. A number draws that many EVENLY SPACED marks
   * (2 ⇒ just the ends); an array places one mark per value, in the slider's
   * own units, so a ruler can mark the presets a chip row also offers.
   */
  ticks?: number | number[];
  /**
   * Print each tick's value under its mark — a numbered ruler rather than bare
   * hairlines. `true` formats with the slider's own `step`; pass a formatter
   * when the ruler wants more precision than the step implies (the width ruler
   * prints "1.50", not "1.5", to match its pill).
   */
  tickLabels?: boolean | ((v: number) => string);
}

function defaultFormat(v: number, step: number): string {
  if (step >= 1) return String(Math.round(v));
  if (step >= 0.1) return v.toFixed(1);
  return v.toFixed(2);
}

/** Tick values in slider units, from either form of the `ticks` prop. */
function tickValues(ticks: number | number[], min: number, max: number): number[] {
  if (Array.isArray(ticks)) return ticks.filter((v) => v >= min && v <= max);
  const n = Math.max(2, Math.round(ticks));
  return Array.from({ length: n }, (_, i) => min + ((max - min) * i) / (n - 1));
}

/**
 * Premium range slider. Fires onChange on every input event (instant viewer
 * updates) and wraps the drag in viewer draft mode automatically — see
 * `./draft`. Outside a `DraftProvider` the draft calls are no-ops.
 */
export function Slider({
  label, min, max, step, value, onChange,
  onDragStart, onDragEnd, draft, display, unit, disabled, className = "",
  minLabel, maxLabel, ticks, tickLabels,
}: SliderProps) {
  const [local, setLocal] = useState(value);
  useEffect(() => setLocal(value), [value]);

  const drag = useDragDraft({ draft, onDragStart, onDragEnd });
  const text = display ? display(local) : defaultFormat(local, step) + (unit ? ` ${unit}` : "");
  const span = max - min || 1;
  const marks = ticks === undefined ? [] : tickValues(ticks, min, max);
  const labelOf = tickLabels
    ? typeof tickLabels === "function" ? tickLabels : (v: number) => defaultFormat(v, step)
    : null;

  return (
    <div className={`flex flex-col gap-2 ${disabled ? "opacity-50" : ""} ${className}`}>
      <div className="flex items-center justify-between">
        <span className="font-display text-[11px] font-medium uppercase tracking-[0.12em] text-on-surface-variant">
          {label}
        </span>
        <span className="font-body text-[13px] tabular-nums text-primary">{text}</span>
      </div>
      <input
        type="range"
        className="lux"
        min={min}
        max={max}
        step={step}
        value={local}
        disabled={disabled}
        {...drag}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          setLocal(v);
          onChange(v);
        }}
      />
      {marks.length ? (
        // Percentage-positioned against the TRACK, which the thumb inset makes
        // a hair wider than the thumb's own travel — a ruler, not a scale to
        // read a value off (that is the number above).
        <div aria-hidden="true" className={`relative -mt-1 ${labelOf ? "h-5" : "h-1.5"}`}>
          {marks.map((v, i) => (
            <span
              key={i}
              className="absolute top-0 flex -translate-x-1/2 flex-col items-center"
              style={{ left: `${((v - min) / span) * 100}%` }}
            >
              <span className="h-1.5 w-px bg-outline-variant" />
              {labelOf ? (
                <span className="mt-1 font-body text-[7px] leading-none tabular-nums text-outline">
                  {labelOf(v)}
                </span>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}
      {minLabel || maxLabel ? (
        <div className="-mt-1 flex justify-between font-display text-[8.5px] font-semibold uppercase tracking-[0.16em] text-outline">
          <span>{minLabel}</span>
          <span>{maxLabel}</span>
        </div>
      ) : null}
    </div>
  );
}
